/**
 * Renders a Contentful `productSubCategoryPage` entry: a metal product page at
 * `/<baseSlug>/<slug>` (for example under `/aluminum`), below a category page (`./Product.tsx`).
 * `pages/[...path].tsx` builds one page per entry under each `productCategoryPage` slug and
 * renders this component when no Contentful `page` claims the same path. The entries are not
 * filtered by brand, so the dormant Southern Tool Steel build exports them too.
 *
 * Page order: `MetaHead`; the hero (`components/Products/SubcategoryHero/HeroDesktop.tsx` with the
 * `SubNavigation` bar from `lg` up, `HeroMobile.tsx` below `lg`); then the same five
 * `AllSections` sections as the category page, with a grade table added to Characteristics &
 * Properties. Headings and labels come from
 * `content/ryerson/products/subCategoryStaticContent.ts` and
 * `components/Products/MetalCapabilities/Content.tsx`. The What Our Customers Say heading and
 * quotes come from `components/Shared/WhatOurCustomers/RefactorContent.tsx` (the carousel picks
 * its own copy). The rest comes from the entry.
 *
 * Links out of the hero, all plain anchors (full page loads): the "Help me find the right metal"
 * button (`/store`), the "Shop Now" button (`shoppingLink`), each Shop By Shape row
 * (`shopByShape[].link`) and the PDFs. `components/Products/SubcategoryHero/Hero.tsx` says where
 * the Contentful links lead.
 *
 * Rules a change must keep:
 * - **Canonical URL.** When the entry has a `shoppingLink`, the canonical is
 *   `NEXT_PUBLIC_ENV_URL` + `shoppingLink` (the store page), otherwise `NEXT_PUBLIC_ENV_URL` +
 *   `/<baseSlug>/<slug>`. These pages are handled specially for SEO per business requirements:
 *   the canonical points at the store, and `marketing-custom-paths.js` keeps the category and
 *   sub-category URLs out of the marketing sitemap. `components/Shared/model/MetaHead.tsx`
 *   records this exception to its default canonical. Unlike `MetaHead`'s default, the value here
 *   doesn't fall back to `""`: with `NEXT_PUBLIC_ENV_URL` unset it starts with `undefined`.
 * - Every field the page reads must be in {@link SubProductFields}, by its full dotted path:
 *   `[...path].tsx` → `getStaticProps` passes the list to `Crawl` as `include`, and `Crawl` drops
 *   any field not listed. Listing a parent alone keeps none of its children.
 * - Each `SubNavigation` id must equal the anchor `components/Shared/AllSections/AllSections.tsx`
 *   gives its section (the title lower-cased, whitespace runs replaced by `-`). Both are built
 *   here from the same copy, so change them together.
 * - The entry needs `generalReferences`, `applications`, `metalCapabilities` and at least one
 *   `characteristicsProperties` item; the components read them without a guard and the page
 *   fails in the browser without them. `materialProperties`, `selectionGuide`, `shopByShape`,
 *   `shoppingLink`, `gradeTableItem` and `byTheNumbers` are optional.
 */
import React from "react";
import { LanguageContent } from "@components/Shared/model/LanguageContent";
import { useApplication } from "@ryerson/frontend.application";
import MetaHead from "@components/Shared/model/MetaHead";
import { AllSections, Sections } from "@components/Shared/AllSections/AllSections";
import SubNavigation, {
	NavigableItem,
} from "@components/BarTubeStructuralProcessing/SubNavigation/SubNavigation";
import ApplicationsComponent from "@components/Products/Applications";
import WhatOurCustomers from "@components/Shared/WhatOurCustomers/WhatOurCustomers";
import { WhatOurContentSays } from "@components/Shared/WhatOurCustomers/RefactorContent";
import CharacteristicsComponent from "@components/Products/Characteristics";
import {
	RyersonMetalCapabilitiesContent,
	StsMetalCapabilitiesContent,
} from "@components/Products/MetalCapabilities/Content";
import MetalCapabilitiesComponent from "@components/Products/MetalCapabilities/MetalCapabilities";
import ByTheNumbersComponent from "@components/Products/ByTheNumbers";
import { MetaContent } from "@components/Shared/model/MetaHead";
import {
	ApplicationsStaticContent,
	ByTheNumbersStaticContent,
	CharacteristicsStaticContent,
	SubCategoryHeroStaticContent,
} from "@content/ryerson/products/subCategoryStaticContent";
import ProductsSubCategoryHeroDesktop from "@components/Products/SubcategoryHero/HeroDesktop";
import { Media } from "@ryerson/frontend.next";
import { companyToCompany } from "@components/Shared/model/Contentful";
import ProductsSubCategoryHeroMobile from "@components/Products/SubcategoryHero/HeroMobile";
import { IContentfulBaseFields } from "./ContentfulPage";

/**
 * One language of a `productSubCategoryPage` entry, as `Crawl` returns it with
 * {@link SubProductFields}. The nested shapes match `components/Products/Types/Types.tsx`.
 */
export interface ContentfulProductSubCategoryPage extends IContentfulBaseFields {
	applications: string[];
	applicationsBlurb: string;
	// The parent category's `slug`: the page is at `/<baseSlug>/<slug>`, and `[...path].tsx`
	// finds a category's sub-categories by this field.
	baseSlug: string;
	// Hero intro text.
	blurb: string;
	byTheNumbers: {
		blurb: string;
		citation: string;
		heading: string;
	}[];
	// Fetched but not rendered.
	catalogBlurb: string;
	characteristicsProperties: {
		description: string;
		title: string;
	}[];
	characteristicsPropertiesBlurb: string;
	// Fetched but not rendered.
	dimensionRanges: {
		dimension: string;
		range: string;
	}[];
	generalReferences: {
		pdf: {
			file: {
				url: string;
			};
		};
		title: string;
	}[];
	// Rows of the grade table in Characteristics & Properties.
	gradeTableItem: {
		characteristics: string;
		grade: string;
		primaryAlloyingElements: string;
	}[];
	// The hero's Material Properties PDFs.
	materialProperties: {
		pdf: {
			file: {
				url: string;
			};
		};
		title: string;
	}[];
	metalCapabilities: {
		// The brands that show this capability (`MetalCapabilities.tsx` filters on it).
		company: ("Ryerson" | "Southern Tool Steel")[];
		description: string;
		image: {
			file: {
				url: string;
			};
		};
		link: string;
		title: string;
	}[];
	// A PDF asset, linked in the hero as "Selection Guide: <page title>". Its own `title` and
	// `description` aren't shown.
	selectionGuide: {
		description: string;
		file: {
			url: string;
		};
		title: string;
	};
	seoDescription: string;
	// `og:image`; its URL has no scheme, so the page prefixes `https:`.
	seoImage: {
		file: {
			url: string;
		};
		title: string;
	};
	seoTitle: string;
	// Rows of the hero's Shop By Shape table.
	shopByShape: {
		grade: string;
		link: string;
		materialShape: string;
		rendering: {
			file: {
				url: string;
			};
		};
		shape: string;
		size: string[];
		type: string;
	}[];
	// Site-relative path of the store page: the "Shop Now" target and, when set, the canonical URL.
	shoppingLink: string;
	// The page's own URL segment, under `baseSlug`.
	slug: string;
	title: string;
}

/**
 * The field paths `pages/[...path].tsx` → `getStaticProps` keeps from a `productSubCategoryPage`
 * response (`Crawl` `include`). A field the page reads must be listed by its full dotted path.
 */
export const SubProductFields = [
	"applications",
	"applicationsBlurb",
	"baseSlug",
	"blurb",
	"byTheNumbers",
	"byTheNumbers.blurb",
	"byTheNumbers.citation",
	"byTheNumbers.heading",
	"catalogBlurb",
	"characteristicsProperties",
	"characteristicsProperties.description",
	"characteristicsProperties.title",
	"characteristicsPropertiesBlurb",
	"dimensionRanges",
	"dimensionRanges.dimension",
	"dimensionRanges.range",
	"generalReferences",
	"generalReferences.pdf",
	"generalReferences.pdf.file",
	"generalReferences.pdf.file.url",
	"generalReferences.title",
	"gradeTableItem",
	"gradeTableItem.characteristics",
	"gradeTableItem.grade",
	"gradeTableItem.primaryAlloyingElements",
	"materialProperties",
	"materialProperties.pdf",
	"materialProperties.pdf.file",
	"materialProperties.pdf.file.url",
	"materialProperties.title",
	"metalCapabilities",
	"metalCapabilities.company",
	"metalCapabilities.description",
	"metalCapabilities.image",
	"metalCapabilities.image.file",
	"metalCapabilities.image.file.url",
	"metalCapabilities.link",
	"metalCapabilities.title",
	"selectionGuide",
	"selectionGuide.description",
	"selectionGuide.file",
	"selectionGuide.file.url",
	"selectionGuide.title",
	"seoDescription",
	"seoImage",
	"seoImage.file",
	"seoImage.file.url",
	"seoImage.title",
	"seoTitle",
	"shopByShape",
	"shopByShape.grade",
	"shopByShape.link",
	"shopByShape.materialShape",
	"shopByShape.rendering",
	"shopByShape.rendering.file",
	"shopByShape.rendering.file.url",
	"shopByShape.shape",
	"shopByShape.size",
	"shopByShape.type",
	"shoppingLink",
	"slug",
	"title",
];

type ProductSubCategoryPageProps = {
	/** The entry in every language (`en`, `fr`, `es`), from `[...path].tsx`. */
	contentfulSubProductPage: LanguageContent<ContentfulProductSubCategoryPage>;
};

/** The product sub-category page body, in the visitor's language; see the file header. */
const ProductSubCategoryPage: React.FC<ProductSubCategoryPageProps> = ({
	contentfulSubProductPage,
}) => {
	const {
		localization: { language },
	} = useApplication();

	const content = contentfulSubProductPage[language];

	const company = companyToCompany(process.env.NEXT_PUBLIC_COMPANY || "");
	const env = process.env.NEXT_PUBLIC_ENV_URL;

	const sections: Sections[] = [];
	const subNav: NavigableItem[] = [];
	// `canonicalUrl` points at the store page when the entry has a `shoppingLink`: special SEO
	// handling for these pages, per business requirements (see the file header).
	let MetaObject: LanguageContent<MetaContent> = {
		en: {
			title: content.seoTitle,
			description: content.seoDescription,
			url: "/" + content.baseSlug + "/" + content.slug,
			imageUrl: content.seoImage?.file?.url
				? `https:${content.seoImage.file.url}`
				: undefined,
			company: false,
			canonicalUrl:
				content.shoppingLink && content.shoppingLink.length > 0
					? env + content.shoppingLink
					: env + "/" + content.baseSlug + "/" + content.slug,
		},
		fr: {
			title: content.seoTitle,
			description: content.seoDescription,
			url: "/" + content.baseSlug + "/" + content.slug,
			imageUrl: content.seoImage?.file?.url
				? `https:${content.seoImage.file.url}`
				: undefined,
			company: false,
			canonicalUrl:
				content.shoppingLink && content.shoppingLink.length > 0
					? env + content.shoppingLink
					: env + "/" + content.baseSlug + "/" + content.slug,
		},
		es: {
			title: content.seoTitle,
			description: content.seoDescription,
			url: "/" + content.baseSlug + "/" + content.slug,
			imageUrl: content.seoImage?.file?.url
				? `https:${content.seoImage.file.url}`
				: undefined,
			company: false,
			canonicalUrl:
				content.shoppingLink && content.shoppingLink.length > 0
					? env + content.shoppingLink
					: env + "/" + content.baseSlug + "/" + content.slug,
		},
	};
	subNav.push({
		id: ApplicationsStaticContent[language].title.toLowerCase().replace(/\s+/g, "-"),
		title: ApplicationsStaticContent[language].navLabel,
	});
	sections.push({
		title: ApplicationsStaticContent[language].title,
		cmp: (
			<ApplicationsComponent
				staticContent={ApplicationsStaticContent[language]}
				dynamicContent={{
					description: content.applicationsBlurb,
					applications: content.applications,
				}}
			/>
		),
	});
	subNav.push({
		id: (
			CharacteristicsStaticContent[language].title1 +
			" " +
			CharacteristicsStaticContent[language].title2
		)
			.toLowerCase()
			.replace(/\s+/g, "-"),
		title: CharacteristicsStaticContent[language].navLabel ?? "",
	});
	sections.push({
		title:
			CharacteristicsStaticContent[language].title1 +
			" " +
			CharacteristicsStaticContent[language].title2,
		cmp: (
			<CharacteristicsComponent
				staticContent={CharacteristicsStaticContent[language]}
				dynamicContent={{
					characteristics: content.characteristicsProperties,
					gradeTableItems: content.gradeTableItem,
					blurb:
						content.characteristicsPropertiesBlurb &&
						content.characteristicsPropertiesBlurb
							? content.characteristicsPropertiesBlurb
							: null,
				}}
			/>
		),
		maxHeight: "10000px",
	});
	subNav.push({
		id: ByTheNumbersStaticContent[language].title.toLowerCase().replace(/\s+/g, "-"),
		title: ByTheNumbersStaticContent[language].navLabel,
	});
	sections.push({
		title: ByTheNumbersStaticContent[language].title,
		cmp: (
			<ByTheNumbersComponent
				staticContent={ByTheNumbersStaticContent[language]}
				dynamicContent={{
					byTheNumbers: content.byTheNumbers,
				}}
			/>
		),
	});
	subNav.push({
		id: WhatOurContentSays[language].title.toLowerCase().replace(/\s+/g, "-"),
		title: WhatOurContentSays[language].title,
	});
	sections.push({
		title: WhatOurContentSays[language].title,
		// The prop-less wrapper around `WhatOurCustomersRefactor`, which `Product.tsx` renders
		// directly; both show the same carousel.
		cmp: <WhatOurCustomers />,
	});
	// The last section's copy depends on the brand: Ryerson's "Metal Capabilities" or Southern Tool
	// Steel's "Our Solutions" (`components/Products/MetalCapabilities/Content.tsx`).
	subNav.push({
		id: (company === "Southern Tool Steel"
			? StsMetalCapabilitiesContent[language]
			: RyersonMetalCapabilitiesContent[language]
		).title
			.toLowerCase()
			.replace(/\s+/g, "-"),
		title: (company === "Southern Tool Steel"
			? StsMetalCapabilitiesContent[language]
			: RyersonMetalCapabilitiesContent[language]
		).navLabel,
	});
	sections.push({
		title: (company === "Southern Tool Steel"
			? StsMetalCapabilitiesContent[language]
			: RyersonMetalCapabilitiesContent[language]
		).title,
		cmp: (
			<MetalCapabilitiesComponent
				staticContent={
					company === "Southern Tool Steel"
						? StsMetalCapabilitiesContent[language]
						: RyersonMetalCapabilitiesContent[language]
				}
				dynamicContent={{
					capabilities: content.metalCapabilities,
				}}
			/>
		),
	});
	return (
		<>
			<MetaHead content={MetaObject[language]} />
			{/* The hero layouts are rendered here directly, not through `SubcategoryHero/Hero.tsx`;
			the `SubNavigation` bar shares the desktop hero's `Media` block. */}
			<Media greaterThanOrEqual="lg">
				<ProductsSubCategoryHeroDesktop
					staticContent={SubCategoryHeroStaticContent[language]}
					dynamicContent={{
						title: content.title,
						blurb: content.blurb,
						references: content.generalReferences,
						properties: content.materialProperties,
						selectionGuide: content.selectionGuide,
						shopByShape: content.shopByShape,
						shopNowLink: content.shoppingLink,
					}}
				/>
				<SubNavigation navigableItems={subNav} type="primary" />
			</Media>
			<Media lessThan="lg">
				<ProductsSubCategoryHeroMobile
					staticContent={SubCategoryHeroStaticContent[language]}
					dynamicContent={{
						title: content.title,
						blurb: content.blurb,
						references: content.generalReferences,
						properties: content.materialProperties,
						selectionGuide: content.selectionGuide,
						shopByShape: content.shopByShape,
						shopNowLink: content.shoppingLink,
					}}
				/>
			</Media>

			<AllSections sections={sections} />
		</>
	);
};

export default ProductSubCategoryPage;
